import { Injectable, OnDestroy } from '@angular/core';
import { PracticeEncounterService } from 'src/app/api/proxy/practice/services/practice-encounter.service';
import { PracticePatientService } from 'src/app/api/proxy/practice/services/practice-patient.service';
import { PromptService } from '../promtp-service/prompt.service';
import {
  EncounterDetailModel,
  EncounterRegionNoteModel,
  FacilityModel,
  NotesModel,
  PatientEncounterDetailModel,
  PatientModel,
  TreatmentModel,
} from 'src/app/api/proxy/practice/practice-models';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { fromEvent, race, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SecureStorageService, StoragePrefixType } from '../secure-storage/secure-storage-service';
import { Events } from '../events/events';
import { PractitionerService } from '../practitioner-service/practitioner.service';
import { TreatmentModeType } from 'src/app/pages/treatment/treatment.page';
import { UserInfoService } from '../auth-service/user-info-service';
import { UserData } from '../auth-service/models/user-data.model';
import { NetworkService } from '../network-service/network-service';
import { EncounterCompleteEncounterPost$Params } from 'src/app/api/proxy/practice/fn/encounter/encounter-complete-encounter-post';
import { PatientPatientEncounterDetailPatientIdGet$Params } from 'src/app/api/proxy/practice/fn/patient/patient-patient-encounter-detail-patient-id-get';
import {
  BaselineSavableTreatmentSnapshot,
  PatientCurrentEncounterCache,
} from './patient-current-encounter-cache';
import { shouldResumeUnlinkedQueuedDraft } from 'src/app/shared/queued-treatment-resume.flow';
import { CaseService } from '../case-service/case.service';
import { v4 as uuidv4 } from 'uuid';
import {
  applyServerTreatmentIds,
  buildTreatmentNoteModelsFromTreatments,
  hasPendingSoapFlushPayload,
  notesHaveSoapContent,
  PendingSoapFlushPayload,
  resolveRegionNotesForPendingFlush,
  resolveSoapNotesForCompleteEncounter,
  soapNoteForCompleteEncounter,
} from './pending-soap.util';
import { isEncounterSameLocalDay } from 'src/app/shared/encounter-same-day';

@Injectable({
  providedIn: 'root',
})
export class TreatmentService implements OnDestroy {
  subscriptions: Subscription[] = [];
  private patientEncountersDetails: PatientEncounterDetailModel | null | undefined;
  private currentReviewEncounterId: string | null = null;
  private patient: PatientModel | null = null;
  private activePatientId: string | null = null;
  private currentEncounterByPatient = new Map<string, PatientCurrentEncounterCache>();
  /**
   * Encounters whose full detail (treatments *and* notes) came from Encounter/{id}.
   * List rows and working-draft snapshots can hold treatments without notes, so
   * "has treatments" alone must not be treated as "fully loaded".
   */
  private hydratedEncounterIds = new Set<string>();

  private _adjustmentMode: TreatmentModeType = TreatmentModeType.Advance;
  private _adjustModeSubject = new BehaviorSubject<TreatmentModeType>(TreatmentModeType.Advance);
  adjustMode$ = this._adjustModeSubject.asObservable();

  _patientModel!: PatientModel;
  _promptService!: PromptService;
  currentPage = 0;
  limit = 10;
  totalEncounters = 0;
  selectedFacility!: FacilityModel | null;
  private _initialized = false;
  private _loadMoreInFlight: Promise<EncounterDetailModel[] | null | undefined> | null = null;

  get adjustmentMode(): TreatmentModeType {
    return this._adjustmentMode;
  }

  set initialized(value: boolean) {
    this._initialized = value;
  }

  set adjustmentMode(mode: TreatmentModeType) {
    this._adjustmentMode = mode;
    this._adjustModeSubject.next(mode);
  }

  static treatmentModelHasAdjustmentData(treatment: TreatmentModel): boolean {
    return (
      treatment.success === true ||
      (treatment.dose != null && treatment.dose > 0) ||
      treatment.mobilityPrePercentage != null ||
      treatment.mobilityPostPercentage != null
    );
  }

  static getSavableTreatments(encounter?: EncounterDetailModel | null): TreatmentModel[] {
    return (encounter?.treatments ?? []).filter((t) =>
      TreatmentService.treatmentModelHasAdjustmentData(t),
    );
  }

  static getTreatmentsForCompleteEncounter(encounter?: EncounterDetailModel | null): TreatmentModel[] {
    return TreatmentService.getSavableTreatments(encounter).map((treatment) => ({ ...treatment }));
  }

  /** Existing treatment rows (by Id) plus pulse rows — used for side/vector/findings dirty checks. */
  static getMetadataComparableTreatments(
    encounter?: EncounterDetailModel | null,
  ): TreatmentModel[] {
    return (encounter?.treatments ?? []).filter(
      (t) => !!t.id || TreatmentService.treatmentModelHasAdjustmentData(t),
    );
  }

  static getTreatmentIdentityKey(treatment: TreatmentModel): string {
    if (treatment.id) {
      return `id:${treatment.id}`;
    }
    return `idx:${treatment.displayIndex ?? 0}:${treatment.bodyPartId ?? 0}:${treatment.dose ?? 0}:${treatment.started ?? ''}`;
  }

  /** @deprecated Use getTreatmentIdentityKey. */
  static getTreatmentKey(treatment: TreatmentModel): string {
    return TreatmentService.getTreatmentIdentityKey(treatment);
  }

  private static normalizeMetadata(value?: string | null): string {
    return (value ?? '').trim();
  }

  private static normalizePartFindings(value?: string | null): string {
    if (!value) {
      return '';
    }
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .sort()
      .join(',');
  }

  static createBaselineSavableTreatmentSnapshot(
    treatment: TreatmentModel,
  ): BaselineSavableTreatmentSnapshot {
    return {
      identityKey: TreatmentService.getTreatmentIdentityKey(treatment),
      bodyPartSide: treatment.bodyPartSide ?? null,
      bodyPartVector: treatment.bodyPartVector ?? null,
      partFindings: treatment.partFindings ?? null,
    };
  }

  static snapshotBaselineSavableTreatments(
    encounter?: EncounterDetailModel | null,
  ): BaselineSavableTreatmentSnapshot[] {
    return TreatmentService.getMetadataComparableTreatments(encounter).map((treatment) =>
      TreatmentService.createBaselineSavableTreatmentSnapshot(treatment),
    );
  }

  static snapshotSavableTreatmentKeys(encounter?: EncounterDetailModel | null): string[] {
    return TreatmentService.getMetadataComparableTreatments(encounter).map((t) =>
      TreatmentService.getTreatmentIdentityKey(t),
    );
  }

  private static treatmentMetadataMatchesBaseline(
    treatment: TreatmentModel,
    baseline: BaselineSavableTreatmentSnapshot,
  ): boolean {
    return (
      TreatmentService.normalizeMetadata(treatment.bodyPartSide) ===
        TreatmentService.normalizeMetadata(baseline.bodyPartSide) &&
      TreatmentService.normalizeMetadata(treatment.bodyPartVector) ===
        TreatmentService.normalizeMetadata(baseline.bodyPartVector) &&
      TreatmentService.normalizePartFindings(treatment.partFindings) ===
        TreatmentService.normalizePartFindings(baseline.partFindings)
    );
  }

  static encounterHasContent(encounter?: EncounterDetailModel | null): boolean {
    if (!encounter?.treatments?.length) {
      return false;
    }
    return encounter.treatments.some(
      (t) => (t.bodyPartId ?? 0) > 0 || TreatmentService.treatmentModelHasAdjustmentData(t),
    );
  }

  static shouldKeepCurrentEncounter(encounter?: EncounterDetailModel | null): boolean {
    return (
      TreatmentService.encounterHasContent(encounter) ||
      TreatmentService.encounterHasSavableTreatments(encounter)
    );
  }

  static encounterHasSavableTreatments(encounter?: EncounterDetailModel | null): boolean {
    return TreatmentService.getSavableTreatments(encounter).length > 0;
  }

  encounterHasNewTreatments(
    encounter: EncounterDetailModel | null | undefined,
    patientId?: string | null,
  ): boolean {
    if (!encounter) {
      return false;
    }

    const resolvedPatientId = patientId ?? this.activePatientId;
    const comparableTreatments = TreatmentService.getMetadataComparableTreatments(encounter);

    if (!encounter.id) {
      return TreatmentService.getSavableTreatments(encounter).length > 0;
    }

    if (!resolvedPatientId) {
      return comparableTreatments.length > 0;
    }

    const cache = this.currentEncounterByPatient.get(resolvedPatientId);
    const baselineTreatments = TreatmentService.resolveBaselineSavableTreatments(cache);
    const baselineByIdentity = new Map(
      baselineTreatments.map((snapshot) => [snapshot.identityKey, snapshot]),
    );
    const compareMetadata = cache?.baselineMetadataCaptured !== false;

    return comparableTreatments.some((treatment) => {
      const identityKey = TreatmentService.getTreatmentIdentityKey(treatment);
      const baseline = baselineByIdentity.get(identityKey);
      if (!baseline) {
        return true;
      }
      if (!compareMetadata) {
        return false;
      }
      return !TreatmentService.treatmentMetadataMatchesBaseline(treatment, baseline);
    });
  }

  private static resolveBaselineSavableTreatments(
    cache?: PatientCurrentEncounterCache | null,
  ): BaselineSavableTreatmentSnapshot[] {
    if (cache?.baselineSavableTreatments?.length) {
      return cache.baselineSavableTreatments;
    }

    return (cache?.baselineSavableTreatmentKeys ?? []).map((identityKey) => ({
      identityKey,
      bodyPartSide: null,
      bodyPartVector: null,
      partFindings: null,
    }));
  }

  isRecallableEncounter(
    encounter: EncounterDetailModel,
    practitionerId: string | null,
    facilityActive: boolean,
    hasRecallPermission: boolean,
  ): boolean {
    if (!encounter.endDate || !encounter.practitioner?.id) {
      return false;
    }

    return (
      isEncounterSameLocalDay(encounter.endDate) &&
      facilityActive &&
      hasRecallPermission &&
      !!practitionerId &&
      encounter.practitioner.id === practitionerId
    );
  }

  isEditableEncounter(
    encounter: EncounterDetailModel,
    practitionerId: string | null,
    facilityActive: boolean,
    hasEditPermission: boolean,
  ): boolean {
    if (!encounter.endDate || !hasEditPermission || !facilityActive) {
      return false;
    }

    return !this.isRecallableEncounter(encounter, practitionerId, facilityActive, true);
  }

  findMostRecentRecallableEncounter(
    encounters: EncounterDetailModel[] | null | undefined,
    practitionerId: string | null,
    facilityActive: boolean,
    hasRecallPermission: boolean,
  ): EncounterDetailModel | null {
    if (!encounters?.length) {
      return null;
    }

    const sorted = [...encounters].sort(
      (a, b) =>
        new Date(b.endDate ?? b.created ?? 0).getTime() -
        new Date(a.endDate ?? a.created ?? 0).getTime(),
    );

    return (
      sorted.find((encounter) =>
        this.isRecallableEncounter(encounter, practitionerId, facilityActive, hasRecallPermission),
      ) ?? null
    );
  }

  async saveAdjustmentMode(): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return;
    }

    await SecureStorageService.saveWithPrefix(
      userId,
      this._adjustmentMode,
      SecureStorageService.Prefix.TreatmentMode,
    );
  }

  async loadSavedAdjustmentMode(): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return;
    }

    const savedMode = await SecureStorageService.getWithPrefix<TreatmentModeType>(
      userId,
      SecureStorageService.Prefix.TreatmentMode,
    );
    if (savedMode !== null && savedMode !== undefined) {
      this.adjustmentMode = savedMode;
    }
  }

  private async getCurrentUserId(): Promise<string | null> {
    const user = await UserInfoService.getUserInfo();
    return user?.sub ?? null;
  }

  constructor(
    protected practicePatientService: PracticePatientService,
    protected practiceEncounterService: PracticeEncounterService,
    protected _practitionerService: PractitionerService,
    protected _networkService: NetworkService,
    protected _caseService: CaseService,
  ) {
    void this.loadSavedAdjustmentMode();

    this.subscriptions.push(
      Events.userUpdated.subscribe(async (user: UserData | null) => {
        if (user?.sub) {
          await this.loadSavedAdjustmentMode();
        } else {
          this.adjustmentMode = TreatmentModeType.Advance;
        }
      }),
    );

    this.subscriptions.push(
      Events.patientSelected.subscribe(async (data: PatientModel) => {
        this.patient = data;
        await this.setActivePatient(data.id!);
        this.resetEncounterListPagination();
        this.clearCurrentReviewEncounter();
        await this.FetchPatientsTreatments(data, this._promptService);
        await this.resolveCurrentEncounterForPatient(data.id!);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((a) => a.unsubscribe());
  }

  getActivePatientId(): string | null {
    return this.activePatientId;
  }

  async setActivePatient(patientId: string): Promise<void> {
    this.activePatientId = patientId;
    if (!this.currentEncounterByPatient.has(patientId)) {
      await this.loadPersistedCurrentEncounter(patientId);
    }
  }

  async ensurePatientCacheLoaded(patientId: string): Promise<void> {
    if (!this.currentEncounterByPatient.has(patientId)) {
      await this.loadPersistedCurrentEncounter(patientId);
    }
  }

  /** Resets encounter-list paging only. Does not clear the active Review encounter position. */
  resetEncounterListPagination(): void {
    this.currentPage = 0;
    this.totalEncounters = 0;
    this.patientEncountersDetails = null;
    this.hydratedEncounterIds.clear();
  }

  clearCurrentReviewEncounter(): void {
    this.currentReviewEncounterId = null;
  }

  clearPatientEncountersDetails(): void {
    this.resetEncounterListPagination();
    this.clearCurrentReviewEncounter();
    if (this.activePatientId) {
      void this.clearCurrentEncounter(this.activePatientId);
    }
  }

  /** Clears all in-memory and persisted caches for the patient session (close → welcome). */
  async clearPatientSession(patientId?: string | null): Promise<void> {
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (resolvedPatientId) {
      await this.clearCurrentEncounter(resolvedPatientId);
    }
    this.resetEncounterListPagination();
    this.clearCurrentReviewEncounter();
    this.patient = null;
    this.activePatientId = null;
  }

  private resumeCurrentEncounterSuppressed = new Set<string>();

  /** After metadata edit, do not re-select that visit (or any visit) as working CURRENT. */
  suppressResumeCurrentEncounter(patientId: string): void {
    this.resumeCurrentEncounterSuppressed.add(patientId);
  }

  allowResumeCurrentEncounter(patientId: string): void {
    this.resumeCurrentEncounterSuppressed.delete(patientId);
  }

  isResumeCurrentEncounterSuppressed(patientId?: string | null): boolean {
    return !!patientId && this.resumeCurrentEncounterSuppressed.has(patientId);
  }

  async clearCurrentEncounter(patientId: string): Promise<void> {
    this.currentEncounterByPatient = new Map<string, PatientCurrentEncounterCache>();
    const scopedKey = await this.buildCurrentEncounterStorageKey(patientId);
    if (scopedKey) {
      await SecureStorageService.removeWithPrefix(
        scopedKey,
        SecureStorageService.Prefix.CurrentEncounter,
      );
    }
    // Also remove legacy CurrentEncounter_{patientId} if present.
    await SecureStorageService.removeWithPrefix(
      patientId,
      SecureStorageService.Prefix.CurrentEncounter,
    );
    this.clearInProgressEncounter();
  }

  /**
   * Removes only the unsaved CURRENT draft row. Keeps PatientEncounterDetail (notes, treatments, findings).
   * Dropping the whole list here left recall with a single hydrated encounter — use
   * resetEncounterListPagination() to discard the list.
   */
  clearInProgressEncounter(): void {
    if (!this.patientEncountersDetails?.encounterDetails) {
      return;
    }
    const inProgressIndex = this.patientEncountersDetails.encounterDetails.findIndex(
      (a) => a.id == null,
    );
    if (inProgressIndex >= 0) {
      this.patientEncountersDetails.encounterDetails.splice(inProgressIndex, 1);
      this.totalEncounters = Math.max(0, this.totalEncounters - 1);
    }
  }

  getCurrentEncounterId(patientId?: string | null): string | null {
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (!resolvedPatientId) {
      return null;
    }
    return this.currentEncounterByPatient.get(resolvedPatientId)?.encounterId ?? null;
  }

  hasWorkingDraft(patientId?: string | null): boolean {
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (!resolvedPatientId) {
      return false;
    }
    return !!this.currentEncounterByPatient.get(resolvedPatientId)?.draftEncounter;
  }

  getCachedQueuedTreatmentId(patientId?: string | null): string | null {
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (!resolvedPatientId) {
      return null;
    }
    return this.currentEncounterByPatient.get(resolvedPatientId)?.queuedTreatmentId ?? null;
  }

  async setCachedQueuedTreatmentId(
    patientId: string,
    queuedTreatmentId: string | null,
  ): Promise<void> {
    const cache = this.getOrCreatePatientCache(patientId);
    cache.queuedTreatmentId = queuedTreatmentId;
    await this.persistCurrentEncounterCache(patientId);
  }

  async shouldResumeUnlinkedQueuedDraft(
    patientId: string,
    queuedTreatmentId: string | null | undefined,
  ): Promise<boolean> {
    if (!queuedTreatmentId) {
      return false;
    }
    await this.ensurePatientCacheLoaded(patientId);
    const cache = this.currentEncounterByPatient.get(patientId);
    return shouldResumeUnlinkedQueuedDraft({
      queuedTreatmentId,
      cachedQueuedTreatmentId: cache?.queuedTreatmentId,
      draftEncounter: cache?.draftEncounter,
    });
  }

  /** True when Adjust wrote a draft/snapshot; false for resolve / id-only priming. */
  wasOpenedInAdjust(patientId?: string | null): boolean {
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (!resolvedPatientId) {
      return false;
    }
    if (this.isResumeCurrentEncounterSuppressed(resolvedPatientId)) {
      return false;
    }
    return !!this.currentEncounterByPatient.get(resolvedPatientId)?.openedInAdjust;
  }

  shouldResumeCurrentEncounterOnAdjust(
    encounter: EncounterDetailModel | null | undefined,
    patientId?: string | null,
  ): boolean {
    if (!encounter || !TreatmentService.shouldKeepCurrentEncounter(encounter)) {
      return false;
    }
    if (!encounter.id) {
      return true;
    }
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (this.wasOpenedInAdjust(resolvedPatientId)) {
      return true;
    }
    return this.encounterHasNewTreatments(encounter, resolvedPatientId);
  }

  private getOrCreatePatientCache(patientId: string): PatientCurrentEncounterCache {
    let cache = this.currentEncounterByPatient.get(patientId);
    if (!cache) {
      cache = {
        patientId,
        encounterId: null,
        draftEncounter: null,
        openedInAdjust: false,
        baselineSavableTreatments: [],
      };
      this.currentEncounterByPatient.set(patientId, cache);
    }
    return cache;
  }

  /** Storage key: CurrentEncounter_{patientId}_{practitionerId}. Null when no current practitioner. */
  private async buildCurrentEncounterStorageKey(patientId: string): Promise<string | null> {
    const user = await UserInfoService.getUserInfo();
    if (!user?.practitionerId) {
      return null;
    }
    return `${patientId}_${user.practitionerId}`;
  }

  private async loadPersistedCurrentEncounter(patientId: string): Promise<void> {
    const user = await UserInfoService.getUserInfo();
    const practitionerId = user?.practitionerId ?? null;
    let persisted: PatientCurrentEncounterCache | null = null;
    let loadedFromLegacy = false;

    if (practitionerId) {
      persisted = await SecureStorageService.getWithPrefix<PatientCurrentEncounterCache>(
        `${patientId}_${practitionerId}`,
        SecureStorageService.Prefix.CurrentEncounter,
      );
    }

    // Legacy CurrentEncounter_{patientId}: adopt only for the current user, then migrate.
    if (!persisted && practitionerId) {
      const legacy = await SecureStorageService.getWithPrefix<PatientCurrentEncounterCache>(
        patientId,
        SecureStorageService.Prefix.CurrentEncounter,
      );
      if (legacy) {
        if (legacy.practitionerId && legacy.practitionerId !== practitionerId) {
          // Belongs to another practitioner — do not resume.
          persisted = null;
        } else {
          persisted = legacy;
          loadedFromLegacy = true;
        }
      }
    }

    if (persisted) {
      const baselineSavableTreatments = persisted.baselineSavableTreatments?.length
        ? persisted.baselineSavableTreatments
        : (persisted.baselineSavableTreatmentKeys ?? []).map((identityKey) => ({
            identityKey,
            bodyPartSide: null,
            bodyPartVector: null,
            partFindings: null,
          }));
      const baselineMetadataCaptured = !!persisted.baselineSavableTreatments?.length;

      const cache: PatientCurrentEncounterCache = {
        ...persisted,
        patientId,
        practitionerId: practitionerId ?? persisted.practitionerId ?? null,
        baselineSavableTreatments,
        baselineMetadataCaptured,
        // Persisted drafts came from an Adjust session that wrote them.
        openedInAdjust: persisted.openedInAdjust ?? !!persisted.draftEncounter,
      };
      this.currentEncounterByPatient.set(patientId, cache);
      if (persisted.draftEncounter) {
        this.assignCorrelationId(persisted.draftEncounter);
        this.ensurePatientEncountersDetails();
        this.updateEncounter(persisted.draftEncounter, !persisted.draftEncounter.id);
      }

      if (loadedFromLegacy && practitionerId) {
        await this.persistCurrentEncounterCache(patientId);
      }
    }
  }

  private async persistCurrentEncounterCache(patientId: string): Promise<void> {
    const cache = this.currentEncounterByPatient.get(patientId);
    if (!cache) {
      return;
    }
    const scopedKey = await this.buildCurrentEncounterStorageKey(patientId);
    if (!scopedKey) {
      return;
    }
    const user = await UserInfoService.getUserInfo();
    cache.practitionerId = user?.practitionerId ?? cache.practitionerId ?? null;
    await SecureStorageService.saveWithPrefix(
      scopedKey,
      cache,
      SecureStorageService.Prefix.CurrentEncounter,
    );
    // Drop legacy key after writing scoped storage so other users cannot resume it.
    await SecureStorageService.removeWithPrefix(
      patientId,
      SecureStorageService.Prefix.CurrentEncounter,
    );
  }

  async persistCurrentEncounterDraft(
    patientId: string,
    encounter: EncounterDetailModel,
  ): Promise<void> {
    const cache = this.getOrCreatePatientCache(patientId);
    cache.encounterId = encounter.id ?? null;
    this.setDraftEncounter(cache, encounter);
    cache.openedInAdjust = true;
    await this.persistCurrentEncounterCache(patientId);
  }

  getPendingSoapNotes(patientId: string): NotesModel | null {
    return this.currentEncounterByPatient.get(patientId)?.pendingSoapNotes ?? null;
  }

  pendingSoapNoteForComplete(patientId: string): NotesModel | undefined {
    return soapNoteForCompleteEncounter(this.getPendingSoapNotes(patientId));
  }

  resolveCompleteEncounterNotes(
    patientId: string,
    encounterNotes: NotesModel | null | undefined,
  ): NotesModel | undefined {
    return resolveSoapNotesForCompleteEncounter(
      encounterNotes,
      this.getPendingSoapNotes(patientId),
    );
  }

  getPendingPartDataPerLevelByLevelName(patientId: string): Record<string, unknown> | null {
    return this.currentEncounterByPatient.get(patientId)?.pendingPartDataPerLevelByLevelName ?? null;
  }

  getPendingRegionPartDataByNoteRegion(patientId: string): Record<string, unknown> | null {
    return this.currentEncounterByPatient.get(patientId)?.pendingRegionPartDataByNoteRegion ?? null;
  }

  async setPendingSoapNotes(patientId: string, notes: NotesModel): Promise<void> {
    const cache = this.getOrCreatePatientCache(patientId);
    cache.pendingSoapNotes = notes;
    await this.persistCurrentEncounterCache(patientId);
  }

  async setPendingPartDataPerLevelByLevelName(
    patientId: string,
    partData: Record<string, unknown> | null,
  ): Promise<void> {
    const cache = this.getOrCreatePatientCache(patientId);
    cache.pendingPartDataPerLevelByLevelName = partData;
    await this.persistCurrentEncounterCache(patientId);
  }

  async setPendingRegionPartDataByNoteRegion(
    patientId: string,
    regionData: Record<string, unknown> | null,
  ): Promise<void> {
    const cache = this.getOrCreatePatientCache(patientId);
    cache.pendingRegionPartDataByNoteRegion = regionData;
    await this.persistCurrentEncounterCache(patientId);
  }

  async clearPendingSoapNotes(patientId: string): Promise<void> {
    const cache = this.currentEncounterByPatient.get(patientId);
    if (!cache || cache.pendingSoapNotes == null) {
      return;
    }

    cache.pendingSoapNotes = null;
    await this.persistCurrentEncounterCache(patientId);
  }

  async clearPendingPartDataPerLevelByLevelName(patientId: string): Promise<void> {
    const cache = this.currentEncounterByPatient.get(patientId);
    if (!cache || cache.pendingPartDataPerLevelByLevelName == null) {
      return;
    }

    cache.pendingPartDataPerLevelByLevelName = null;
    await this.persistCurrentEncounterCache(patientId);
  }

  private resolvePendingFlushSnapshot(patientId: string): {
    notes: NotesModel | null;
    treatments: TreatmentModel[];
    regionNotes: EncounterRegionNoteModel[] | null;
  } {
    const cache = this.currentEncounterByPatient.get(patientId);
    return {
      notes: this.resolveCompleteEncounterNotes(patientId, cache?.draftEncounter?.notes) ?? null,
      treatments: cache?.draftEncounter?.treatments ?? [],
      regionNotes: resolveRegionNotesForPendingFlush({
        draftRegionNotes: cache?.draftEncounter?.regionNotes,
        pendingRegionPartDataByNoteRegion: cache?.pendingRegionPartDataByNoteRegion,
      }),
    };
  }

  async stashPendingSoapForOfflineComplete(patientId: string, correlationId: string): Promise<void> {
    const snapshot = this.resolvePendingFlushSnapshot(patientId);
    if (!hasPendingSoapFlushPayload(snapshot)) {
      return;
    }

    const payload: PendingSoapFlushPayload = {
      patientId,
      notes: snapshot.notes ?? {},
      treatments: snapshot.treatments,
      regionNotes: snapshot.regionNotes,
    };
    await SecureStorageService.saveWithPrefix(
      `pendingSoap_${correlationId}`,
      payload,
      SecureStorageService.Prefix.CurrentEncounter,
    );
  }

  async flushPendingSoapAfterComplete(
    patientId: string,
    completed: EncounterDetailModel,
  ): Promise<void> {
    const encounterId = completed.id;
    if (!encounterId) {
      return;
    }

    const snapshot = this.resolvePendingFlushSnapshot(patientId);
    let notes = snapshot.notes;
    let localTreatments = snapshot.treatments;
    let regionNotes = completed.regionNotes ?? snapshot.regionNotes;

    if (!hasPendingSoapFlushPayload({ notes, treatments: localTreatments, regionNotes })) {
      const stashed = await this.loadStashedPendingSoap(completed.correlationId);
      if (stashed) {
        notes = stashed.notes;
        localTreatments = stashed.treatments;
        regionNotes = stashed.regionNotes ?? regionNotes;
      }
    }

    if (!hasPendingSoapFlushPayload({ notes, treatments: localTreatments, regionNotes })) {
      return;
    }

    let serverTreatments = completed.treatments ?? [];
    if (!serverTreatments.length) {
      try {
        const fullEncounter = await firstValueFrom(
          this.practiceEncounterService.encounterIdGet({ id: encounterId }),
        );
        serverTreatments = fullEncounter?.treatments ?? [];
        if (fullEncounter?.patientCaseId && !completed.patientCaseId) {
          completed.patientCaseId = fullEncounter.patientCaseId;
        }
      } catch (error) {
        console.error('Failed to load treatments for SOAP findings flush', error);
      }
    }

    const treatmentsWithIds = applyServerTreatmentIds(localTreatments, serverTreatments);
    const treatmentNotes = buildTreatmentNoteModelsFromTreatments(treatmentsWithIds);
    if (!notesHaveSoapContent(notes) && !treatmentNotes && !regionNotes?.length) {
      return;
    }

    if (!notes) {
      notes = {};
    }

    const savedResponse = await this._caseService.saveEncounterNote(encounterId, notes, {
      treatmentNotes,
      regionNotes,
    });
    // SOAP resolution reads the loaded encounter list instead of re-fetching Encounter/{id},
    // so the flushed note has to land on the listed encounter as well.
    if (savedResponse?.note && this.getEncounterById(encounterId)) {
      this.updateEncounter({ id: encounterId, notes: savedResponse.note });
    }
    await this.clearPendingSoapNotes(patientId);
    await this.clearStashedPendingSoap(completed.correlationId);
  }

  private async loadStashedPendingSoap(
    correlationId?: string | null,
  ): Promise<PendingSoapFlushPayload | null> {
    if (!correlationId) {
      return null;
    }

    return (
      (await SecureStorageService.getWithPrefix<PendingSoapFlushPayload>(
        `pendingSoap_${correlationId}`,
        SecureStorageService.Prefix.CurrentEncounter,
      )) ?? null
    );
  }

  private async clearStashedPendingSoap(correlationId?: string | null): Promise<void> {
    if (!correlationId) {
      return;
    }

    await SecureStorageService.removeWithPrefix(
      `pendingSoap_${correlationId}`,
      SecureStorageService.Prefix.CurrentEncounter,
    );
  }

  async updateCurrentEncounterCache(
    patientId: string,
    encounter: EncounterDetailModel,
  ): Promise<void> {
    const cache = this.getOrCreatePatientCache(patientId);
    cache.encounterId = encounter.id ?? null;
    this.setDraftEncounter(cache, encounter);
    cache.openedInAdjust = true;
    this.updateEncounter(encounter, !encounter.id);
    await this.persistCurrentEncounterCache(patientId);
  }

  /**
   * Working copies are rebuilt from the playlist and can omit notes. Keep notes already
   * resolved for the same encounter so a snapshot cannot blank the SOAP modal.
   */
  private setDraftEncounter(
    cache: PatientCurrentEncounterCache,
    encounter: EncounterDetailModel,
  ): void {
    const previous = cache.draftEncounter;
    const draft: EncounterDetailModel = { ...encounter };
    if (!draft.notes && previous?.notes && (previous.id ?? null) === (draft.id ?? null)) {
      draft.notes = previous.notes;
    }
    cache.draftEncounter = draft;
  }

  private getWorkingEncounter(
    patientId: string,
    encounterId?: string | null,
  ): EncounterDetailModel | null {
    const cache = this.currentEncounterByPatient.get(patientId);
    const working = cache?.draftEncounter;
    if (!working) {
      return null;
    }

    if (encounterId != null && working.id !== encounterId) {
      return null;
    }

    return working;
  }

  private ensurePatientEncountersDetails(): void {
    if (!this.patientEncountersDetails) {
      this.patientEncountersDetails = {
        encounterDetails: [],
        totalEncounters: 0,
      };
    }
    if (!this.patientEncountersDetails.encounterDetails) {
      this.patientEncountersDetails.encounterDetails = [];
    }
  }

  private encounterHasTreatments(encounter?: EncounterDetailModel | null): boolean {
    return !!encounter?.treatments?.length;
  }

  /** Treatments alone do not prove a full payload — notes only arrive on Encounter/{id}. */
  private isEncounterFullyLoaded(encounter?: EncounterDetailModel | null): boolean {
    return (
      !!encounter?.id &&
      this.hydratedEncounterIds.has(encounter.id) &&
      this.encounterHasTreatments(encounter)
    );
  }

  /**
   * Patient/PatientEncounterDetail already projects treatments, part findings, and SOAP notes.
   * Copy any of those that a working draft is missing so recall/SOAP do not GET Encounter/{id}.
   */
  private applyListedEncounterPayload(
    target: EncounterDetailModel,
    listed?: EncounterDetailModel | null,
  ): void {
    if (!listed) {
      return;
    }
    if (!target.notes && listed.notes) {
      target.notes = listed.notes;
    }
    if (!target.regionNotes?.length && listed.regionNotes?.length) {
      target.regionNotes = listed.regionNotes;
    }
    if (!this.encounterHasTreatments(target) && this.encounterHasTreatments(listed)) {
      target.treatments = listed.treatments;
    }
  }

  async ensureEncounterLoaded(
    encounterId: string,
    patientId?: string | null,
  ): Promise<EncounterDetailModel | null> {
    const listed = this.getEncounterById(encounterId);
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (resolvedPatientId) {
      const working = this.getWorkingEncounter(resolvedPatientId, encounterId);
      if (working) {
        this.applyListedEncounterPayload(working, listed);
        return working;
      }
    }

    // Listed rows from PatientEncounterDetail are fully hydrated (notes + treatments + findings).
    if (listed) {
      return listed;
    }

    try {
      const encounter = await firstValueFrom(
        this.practiceEncounterService.encounterIdGet({ id: encounterId }),
      );
      if (encounter?.id) {
        this.hydratedEncounterIds.add(encounter.id);
        this.updateEncounter(encounter);
        return encounter;
      }
    } catch {
      return listed ?? null;
    }

    return listed ?? null;
  }

  /**
   * SOAP notes only arrive on the Encounter/{id} payload. Review can be holding a list row or
   * an Adjust working-draft snapshot that carries treatments but no notes, so resolve them on
   * demand and backfill every in-memory copy without touching unsaved treatment state.
   */
  async ensureEncounterNotesLoaded(encounterId: string): Promise<NotesModel | null> {
    const loaded = this.findLoadedEncounterNotes(encounterId);
    if (loaded) {
      return loaded;
    }

    try {
      const encounter = await firstValueFrom(
        this.practiceEncounterService.encounterIdGet({ id: encounterId }),
      );
      const notes = encounter?.notes ?? null;
      if (notes) {
        this.applyEncounterNotes(encounterId, notes);
      }
      return notes;
    } catch {
      return null;
    }
  }

  private findLoadedEncounterNotes(encounterId: string): NotesModel | null {
    const row = this.getEncounterById(encounterId);
    if (row?.notes) {
      return row.notes;
    }

    for (const cache of this.currentEncounterByPatient.values()) {
      const draft = cache.draftEncounter;
      if (draft?.id === encounterId && draft.notes) {
        return draft.notes;
      }
    }

    return null;
  }

  /** Notes-only backfill: never replaces treatments, so unsaved recall edits survive. */
  private applyEncounterNotes(encounterId: string, notes: NotesModel): void {
    const row = this.getEncounterById(encounterId);
    if (row) {
      row.notes = notes;
    }

    this.currentEncounterByPatient.forEach((cache) => {
      if (cache.draftEncounter?.id === encounterId) {
        cache.draftEncounter.notes = notes;
      }
    });
  }

  async setCurrentEncounter(
    encounterId: string | null | undefined,
    patientId?: string | null,
    encounterSnapshot?: EncounterDetailModel | null,
    options?: { preserveBaseline?: boolean },
  ): Promise<void> {
    const resolvedPatientId = patientId ?? this.activePatientId;
    if (!resolvedPatientId) {
      return;
    }

    const cache = this.getOrCreatePatientCache(resolvedPatientId);
    const previousEncounterId = cache.encounterId ?? null;

    if (!encounterId) {
      cache.encounterId = null;
      if (previousEncounterId != null) {
        cache.pendingSoapNotes = null;
        cache.pendingPartDataPerLevelByLevelName = null;
      }
      if (encounterSnapshot) {
        this.assignCorrelationId(encounterSnapshot);
        cache.draftEncounter = encounterSnapshot;
        cache.openedInAdjust = true;
        cache.baselineSavableTreatments = [];
        cache.baselineMetadataCaptured = true;
        this.updateEncounter(encounterSnapshot, true);
      } else {
        cache.draftEncounter = null;
        cache.openedInAdjust = false;
        cache.baselineSavableTreatments = [];
        cache.baselineMetadataCaptured = true;
      }
      await this.persistCurrentEncounterCache(resolvedPatientId);
      return;
    }

    const encounter =
      encounterSnapshot ?? (await this.ensureEncounterLoaded(encounterId, resolvedPatientId));
    if (encounter?.id) {
      if (previousEncounterId !== encounter.id) {
        cache.pendingSoapNotes = null;
        cache.pendingPartDataPerLevelByLevelName = null;
      }
      cache.encounterId = encounter.id;
      this.setDraftEncounter(cache, encounter);
      // Snapshot means Adjust (or equivalent) wrote the working copy; id-only is resolve/priming.
      cache.openedInAdjust = encounterSnapshot != null;
      if (!options?.preserveBaseline) {
        cache.baselineSavableTreatments =
          TreatmentService.snapshotBaselineSavableTreatments(encounter);
        cache.baselineMetadataCaptured = true;
      }
      this.updateEncounter(encounter);
      await this.persistCurrentEncounterCache(resolvedPatientId);
    }
  }

  async setCurrentReviewEncounter(encounterId: string | null | undefined) {
    if (!encounterId) {
      this.currentReviewEncounterId = null;
      return;
    }

    const alreadyHydrated =
      this.currentReviewEncounterId === encounterId &&
      this.isEncounterFullyLoaded(this.getEncounterById(encounterId));

    // Always remember the Review position, even if the encounter list is temporarily empty
    // (e.g. profile refreshed the list) or hydration is still in flight.
    this.currentReviewEncounterId = encounterId;

    if (alreadyHydrated) {
      return;
    }

    const patientId = this.activePatientId;
    if (patientId) {
      const workingDraft = this.getWorkingEncounter(patientId, encounterId);
      if (workingDraft && TreatmentService.shouldKeepCurrentEncounter(workingDraft)) {
        return;
      }
    }

    if (this.patientEncountersDetails?.encounterDetails) {
      const preloadedEncounter = this.patientEncountersDetails.encounterDetails.find(
        (a) => a.id == encounterId,
      );
      if (preloadedEncounter) {
        // List rows may omit treatments/notes; hydrate once here so Review SetupPage avoids a second racey fetch.
        if (!this.isEncounterFullyLoaded(preloadedEncounter)) {
          await this.ensureEncounterLoaded(encounterId, patientId);
        }
        return;
      }

      await this.ensureEncounterLoaded(encounterId, patientId);
    }
  }

  async getCurrentReviewEncouter(): Promise<EncounterDetailModel | null> {
    const patientId = this.activePatientId;

    const resolveWorkingDraft = async (): Promise<EncounterDetailModel | null> => {
      if (!patientId) {
        return null;
      }
      return this.getCurrentEncounter(patientId, { allowWhenUninitialized: true });
    };

    // Adjust → Review: list rows can lag the working copy (P.A.R.T / side / vector).
    if (patientId && this.currentReviewEncounterId == null && this.wasOpenedInAdjust(patientId)) {
      const working = await resolveWorkingDraft();
      if (working && TreatmentService.shouldKeepCurrentEncounter(working)) {
        return working;
      }
    }

    const encounterDetails = this.patientEncountersDetails?.encounterDetails;
    if (!encounterDetails?.length) {
      if (patientId && this.currentReviewEncounterId == null) {
        return (await resolveWorkingDraft()) ?? null;
      }
      return null;
    }

    let reviewEncounter = encounterDetails.find((a) => a.id == this.currentReviewEncounterId);
    if (reviewEncounter) {
      if (patientId && this.wasOpenedInAdjust(patientId)) {
        const working = this.getWorkingEncounter(patientId, reviewEncounter.id ?? null);
        if (working && (working.id ?? null) === (reviewEncounter.id ?? null)) {
          return working;
        }
      }
      return reviewEncounter;
    }

    if (this.currentReviewEncounterId == null) {
      const working = await resolveWorkingDraft();
      if (working?.id == null && TreatmentService.shouldKeepCurrentEncounter(working)) {
        return working;
      }

      const fallbackId = encounterDetails[0].id;
      if (!fallbackId) {
        return working ?? null;
      }
      this.currentReviewEncounterId = fallbackId;
      reviewEncounter = encounterDetails.find((a) => a.id == fallbackId);
      if (reviewEncounter) {
        return reviewEncounter;
      }
    }

    await this.setCurrentReviewEncounter(this.currentReviewEncounterId);
    return (
      this.patientEncountersDetails?.encounterDetails?.find(
        (a) => a.id == this.currentReviewEncounterId,
      ) ?? null
    );
  }

  getEncounterById(id: string): EncounterDetailModel | undefined {
    if (this.patientEncountersDetails) {
      let encounter = this.patientEncountersDetails.encounterDetails?.find((a) => a.id == id);
      return encounter;
    }

    return undefined;
  }

  //here
  async getCurrentEncounter(
    patientId?: string | null,
    options?: { allowWhenUninitialized?: boolean },
  ): Promise<EncounterDetailModel | null> {
    if (this._initialized === false && !options?.allowWhenUninitialized) {
      return null;
    }

    const resolvedPatientId = patientId ?? this.activePatientId;
    if (!resolvedPatientId) {
      return null;
    }

    const cache = this.currentEncounterByPatient.get(resolvedPatientId);
    if (cache?.draftEncounter) {
      return cache.draftEncounter;
    }

    if (cache?.encounterId != null) {
      return await this.ensureEncounterLoaded(cache.encounterId, resolvedPatientId);
    }

    if (this.patientEncountersDetails?.encounterDetails) {
      const inProgressEncounter = this.patientEncountersDetails.encounterDetails.find(
        (a) => a.id == null,
      );
      if (inProgressEncounter) {
        return inProgressEncounter;
      }
    }

    return null;
  }

  async resolveCurrentEncounterForPatient(
    patientId: string,
    options?: {
      practitionerId?: string | null;
      facilityActive?: boolean;
      hasRecallPermission?: boolean;
    },
  ): Promise<void> {
    await this.setActivePatient(patientId);

    if (this.isResumeCurrentEncounterSuppressed(patientId)) {
      await this.setCurrentEncounter(null, patientId);
      return;
    }

    const current = await this.getCurrentEncounter(patientId);
    if (current && TreatmentService.shouldKeepCurrentEncounter(current)) {
      return;
    }

    const practitionerId =
      options?.practitionerId ?? (await UserInfoService.getUserInfo())?.practitionerId ?? null;
    const facilityActive = options?.facilityActive ?? this._practitionerService.facilityActive;
    const hasRecallPermission = options?.hasRecallPermission ?? true;

    const encounters = await this.getPatientEncounters();
    const recallable = this.findMostRecentRecallableEncounter(
      encounters ?? [],
      practitionerId,
      facilityActive,
      hasRecallPermission,
    );

    if (recallable?.id) {
      await this.setCurrentEncounter(recallable.id, patientId);
    } else {
      await this.setCurrentEncounter(null, patientId);
    }
  }

  getPatientEncountersDetail(): PatientEncounterDetailModel | null | undefined {
    return this.patientEncountersDetails;
  }

  async getPatientEncounters() {
    return this.patientEncountersDetails?.encounterDetails?.sort(
      (a, b) => new Date(b.created!).getTime() - new Date(a.created!).getTime(),
    );
  }

  async FetchPatientsTreatments(
    patientModel: PatientModel,
    promptService: PromptService,
  ): Promise<EncounterDetailModel[] | null | undefined> {
    this._patientModel = patientModel;
    this._promptService = promptService;
    this.selectedFacility = await this._practitionerService.getSelectedFacility();
    try {
      const request: PatientPatientEncounterDetailPatientIdGet$Params = {
        patientId: patientModel.id!,
        index: this.currentPage,
        pageSize: this.limit,
      };

      const patientEncounterDetail = await firstValueFrom(
        this._networkService.performNetworkCacheFallbackCall(
          StoragePrefixType.Encounter,
          // Include page so LoadMore does not reuse page-0 cache and add zero new rows.
          `${patientModel.id!}:p${this.currentPage}:s${this.limit}`,
          this.practicePatientService.patientPatientEncounterDetailPatientIdGet(request),
        ),
      );

      if (
        patientEncounterDetail &&
        patientEncounterDetail.totalEncounters != null &&
        patientEncounterDetail.encounterDetails
      ) {
        if (this.patientEncountersDetails?.encounterDetails) {
          patientEncounterDetail.encounterDetails.forEach((encounter) => {
            if (
              !this.patientEncountersDetails!.encounterDetails!.some((e) => e.id === encounter.id)
            ) {
              this.patientEncountersDetails!.encounterDetails!.push(encounter);
            }
          });
        } else {
          this.patientEncountersDetails = { ...patientEncounterDetail };
        }

        if (this.currentPage === 0) {
          this.reconcileWorkingDraftIntoEncounterList();
        }

        const details = this.patientEncountersDetails?.encounterDetails ?? [];
        const hasLocalDraft = details.some((e) => e.id == null);
        // Keep local CURRENT draft in the total; API totals omit unsaved drafts and
        // must not shrink below the encounters already held in memory.
        this.totalEncounters = Math.max(
          (patientEncounterDetail.totalEncounters ?? 0) + (hasLocalDraft ? 1 : 0),
          details.length,
        );
      } else if (this.currentPage === 0) {
        this.totalEncounters = 0;
        this.patientEncountersDetails = null;
      }
      if (!this.patient) this.patient = patientModel;

      return patientEncounterDetail?.encounterDetails;
    } catch (error) {
      promptService.showNetworkConnectionError(error, 'patient-encounters-details-list');
      if (this.currentPage === 0) {
        this.patientEncountersDetails = null;
      }
      return null;
    }
  }

  async LoadMoreEncounters(): Promise<EncounterDetailModel[] | null | undefined> {
    if (this._loadMoreInFlight) {
      return this._loadMoreInFlight;
    }

    this._loadMoreInFlight = (async () => {
      const beforeCount = this.patientEncountersDetails?.encounterDetails?.length ?? 0;
      this.currentPage++;
      const fetched = await this.FetchPatientsTreatments(this._patientModel, this._promptService);
      const afterCount = this.patientEncountersDetails?.encounterDetails?.length ?? 0;
      // Roll back page index when nothing new was appended (cache miss / empty / all duplicates).
      if (afterCount <= beforeCount) {
        this.currentPage = Math.max(0, this.currentPage - 1);
        return [];
      }
      return fetched;
    })();

    try {
      return await this._loadMoreInFlight;
    } finally {
      this._loadMoreInFlight = null;
    }
  }

  /** Re-insert the Adjust working draft after API list refresh so Review sees metadata. */
  private reconcileWorkingDraftIntoEncounterList(): void {
    const patientId = this.activePatientId;
    if (!patientId || !this.wasOpenedInAdjust(patientId)) {
      return;
    }

    const working = this.currentEncounterByPatient.get(patientId)?.draftEncounter;
    if (working) {
      this.updateEncounter(working, !working.id);
    }
  }

  updateEncounter(encounter: EncounterDetailModel, pushToTop = false) {
    this.ensurePatientEncountersDetails();

    const details = this.patientEncountersDetails!.encounterDetails!;
    const currentEncounter = encounter.id
      ? details.find((a) => a.id === encounter.id)
      : details.find((a) => a.id == null);

    if (currentEncounter) {
      // Rows are merged from payloads that omit notes; do not blank resolved notes.
      const previousNotes = currentEncounter.notes;
      Object.assign(currentEncounter, encounter);
      if (!currentEncounter.notes && previousNotes) {
        currentEncounter.notes = previousNotes;
      }
    } else {
      if (pushToTop) {
        details.unshift(encounter);
      } else {
        details.push(encounter);
      }

      this.totalEncounters++;
    }

    if (this.activePatientId) {
      const cache = this.currentEncounterByPatient.get(this.activePatientId);
      if (!cache) {
        return;
      }

      const isCurrentDraft = !encounter.id && cache.encounterId == null;
      const isCurrentSaved = !!encounter.id && cache.encounterId === encounter.id;
      if (isCurrentDraft || isCurrentSaved) {
        this.setDraftEncounter(cache, encounter);
        void this.persistCurrentEncounterCache(this.activePatientId);
      }
    }
  }

  async syncEncounter(
    request: EncounterCompleteEncounterPost$Params,
    signal?: AbortSignal,
  ): Promise<void> {
    const request$ = this.practiceEncounterService.encounterCompleteEncounterPost(request);
    let result: EncounterDetailModel | undefined;
    if (signal) {
      const abort$ = fromEvent(signal, 'abort').pipe(
        take(1),
        switchMap(() => throwError(() => new DOMException('Aborted', 'AbortError'))),
      );
      result = await firstValueFrom(race(request$, abort$));
    } else {
      result = await firstValueFrom(request$);
    }

    const patientId = request.body?.patientId;
    if (result?.id && patientId) {
      try {
        await this.flushPendingSoapAfterComplete(patientId, {
          ...result,
          correlationId: result.correlationId ?? request.body?.correlationId,
        });
      } catch (flushError) {
        console.error('Failed to flush SOAP notes after synced encounter', flushError);
      }
    }
  }

  /**
   * Assigns a correlationId when creating a new local encounter. Does not look up or reuse
   * ids from the unsynced queue — each new encounter gets its own id at creation time.
   */
  assignCorrelationId(encounter: EncounterDetailModel): string {
    if (!encounter.correlationId) {
      encounter.correlationId = uuidv4();
    }
    return encounter.correlationId;
  }

  /**
   * Attach the in-progress queued visit id to CompleteEncounter bodies (including UnsyncedEncounter cache)
   * so offline replay can link the queue on the server after the encounter is saved.
   */
  applyCachedQueuedTreatmentIdToCompleteEncounterBody(
    patientId: string | null | undefined,
    body: Record<string, unknown>,
  ): void {
    if (!patientId) {
      return;
    }
    const queueId = this.getCachedQueuedTreatmentId(patientId)?.trim();
    if (!queueId) {
      return;
    }
    body['patientTreatmentQueueId'] = queueId;
  }

  async updateEncounterNotes(encounterId: string, note: NotesModel): Promise<NotesModel> {
    const saved = await firstValueFrom(
      this.practiceEncounterService.encounterUpdateNotesPost({
        body: {
          encounterId,
          note,
        },
      }),
    );
    // Keep the list row and any working draft in step so re-opening SOAP shows the saved note.
    this.applyEncounterNotes(encounterId, saved ?? note);
    return saved;
  }

  /**
   * Completes the persisted draft via online POST or UnsyncedEncounter queue.
   * @returns true when durable (server accepted or queue write succeeded); false on failure.
   */
  async completeCachedEncounter(patientId: string, facilityId: string): Promise<boolean> {
    const encounter = await this.getCurrentEncounter(patientId, {
      allowWhenUninitialized: true,
    });
    if (!encounter || !TreatmentService.encounterHasSavableTreatments(encounter)) {
      return false;
    }

    const user = await UserInfoService.getUserInfo();
    if (!user?.practitionerId) {
      return false;
    }

    const hadCorrelationId = !!encounter.correlationId;
    const correlationId = this.assignCorrelationId(encounter);
    if (!hadCorrelationId) {
      const cache = this.getOrCreatePatientCache(patientId);
      cache.draftEncounter = { ...encounter };
      await this.persistCurrentEncounterCache(patientId);
    }

    const request: EncounterCompleteEncounterPost$Params = {
      body: {
        id: encounter.id,
        correlationId,
        facilityId,
        patientId,
        patientCaseId: encounter.patientCaseId,
        practitionerId: user.practitionerId,
        start: encounter.startDate ?? new Date().toISOString(),
        treatments: TreatmentService.getTreatmentsForCompleteEncounter(encounter),
        notes: this.resolveCompleteEncounterNotes(patientId, encounter.notes),
      },
    };
    this.applyCachedQueuedTreatmentIdToCompleteEncounterBody(patientId, request.body as Record<string, unknown>);

    try {
      const result = await firstValueFrom(
        this._networkService.performNetworkPostCacheFallbackCall<
          EncounterDetailModel,
          EncounterCompleteEncounterPost$Params
        >(
          StoragePrefixType.UnsyncedEncounter,
          `${user.practitionerId}_${correlationId}`,
          request,
          (req) => this.practiceEncounterService.encounterCompleteEncounterPost(req),
        ),
      );

      if (result?.id) {
        try {
          await this.flushPendingSoapAfterComplete(patientId, {
            ...result,
            correlationId: result.correlationId ?? correlationId,
          });
        } catch (flushError) {
          console.error('Failed to flush SOAP notes after complete encounter', flushError);
        }
        if (result.notes) {
          encounter.notes = result.notes;
        } else if (encounter.notes) {
          result.notes = encounter.notes;
        }
        this.updateEncounter(result);
        const cache = this.getOrCreatePatientCache(patientId);
        cache.encounterId = result.id;
        cache.draftEncounter = { ...result };
        await this.persistCurrentEncounterCache(patientId);
      } else {
        await this.stashPendingSoapForOfflineComplete(patientId, correlationId);
      }

      // null = queued offline; object = online — both are durable success.
      return true;
    } catch {
      return false;
    }
  }
}
